// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A dialog component for removing assets from projects.
 * This module provides a focused interface for unassigning assets
 * from their current projects, with support for date validation
 * and history tracking. It ensures unassignments follow business
 * rules and maintains data consistency.
 *
 * Key features:
 * - Asset validation
 * - Date constraints
 * - History tracking
 * - Error handling
 * - Real-time updates
 */
import { getAssetProjectHistory } from '@/asset/api/get-asset-project-history';
import { Asset, SparseAsset } from '@/asset/api/types';
import { FormSparseAssetSearch } from '@/asset/components/form-sparse-asset-search';
import { FormDatePicker } from '@/components/form-date-picker';
import { SupportSnackbar } from '@/components/support-snackbar';
import { MINIMUM_DATE } from '@/constants/date-shortcut';
import { unassignAssets } from '@/project/api/unassign-assets';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Schema for validating asset data.
 * Uses passthrough since the asset comes from props and is already validated.
 */
const assetSchema = z.custom<SparseAsset>();

/**
 * Schema for validating the unassignment date.
 * Ensures a valid date is provided that respects
 * the original assignment date.
 */
const requiredDateSchema = dateSchema
  .nullable()
  .refine((date): date is DateTime => date !== null && date.isValid, {
    message: 'Effective date is required',
  })
  .refine((date) => date >= MINIMUM_DATE, {
    message: 'Effective date cannot be before January 1, 1970',
  });

/**
 * Interface for the raw form input data.
 * Represents the unvalidated state of the unassignment form.
 *
 * @interface InputData
 */
interface InputData {
  /**
   * The asset to be unassigned.
   * Can be null when no asset is selected.
   */
  asset: z.infer<typeof assetSchema> | null;

  /**
   * The effective date of the unassignment.
   * Can be null when no date is selected.
   */
  date: DateTime | null;
}

/**
 * Schema for validating form output data.
 * Enforces required fields and data format.
 */
const outputSchema = z
  .object({
    asset: assetSchema,
    date: requiredDateSchema,
  })
  .superRefine((data, context) => {
    const latestAllowedDate = DateTime.now().plus({ days: 1 }).endOf('day');

    if (data.date > latestAllowedDate) {
      context.addIssue({
        path: ['date'],
        code: z.ZodIssueCode.custom,
        message: 'Assets cannot be unassigned in the future',
      });
    }
  });

/**
 * Type definition for validated form output.
 * Used when submitting the form data.
 *
 * @typedef {Object} OutputData
 */
type OutputData = z.infer<typeof outputSchema>;

/**
 * Props for configuring the AssetUnassignmentDialog component.
 *
 * @interface Props
 */
interface Props {
  /**
   * The asset to be unassigned.
   * Used as the default value in the form.
   */
  asset: Asset;

  /**
   * If true, the dialog is open and visible.
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog requests to be closed.
   */
  onClose: () => void;
}

/**
 * A dialog component for unassigning assets from projects.
 * Provides a form interface for selecting an effective date
 * and confirming the unassignment, with validation and
 * history tracking.
 *
 * Features:
 * - Asset selection with validation
 * - Date picking with constraints
 * - Project history display
 * - Form state management
 * - Error message display
 * - Real-time query invalidation
 *
 * Usage example:
 * ```tsx
 * function AssetView({ asset }: { asset: Asset }) {
 *   const [isOpen, setIsOpen] = useState(false);
 *
 *   return (
 *     <AssetUnassignmentDialog
 *       asset={asset}
 *       isOpen={isOpen}
 *       onClose={() => setIsOpen(false)}
 *     />
 *   );
 * }
 * ```
 */
export function AssetUnassignmentDialog(props: Props) {
  const queryClient = useQueryClient();

  const unassignAssetMutation = useMutation({
    mutationFn: (data: OutputData) => {
      if (props.asset.project === null) {
        throw new Error('A project is required to unassign an asset', {
          cause: props.asset,
        });
      }

      return unassignAssets(props.asset.project.key, [
        { assetId: props.asset.id, date: data.date },
      ]);
    },
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['asset'] });

        if (props.asset.project) {
          queryClient.invalidateQueries({
            queryKey: ['project', props.asset.project.key],
          });
        }

        close();
        reset();
      }
    },
  });

  // Extract sparse asset fields from the full asset for form validation
  const sparseAsset = {
    id: props.asset.id,
    key: props.asset.key,
    serial: props.asset.serial,
    item: props.asset.item,
    functionalStatus: props.asset.functionalStatus,
    isInTransit: props.asset.isInTransit,
    inInventoryDate: props.asset.inInventoryDate,
  };

  const { control, reset, handleSubmit } = useForm<
    InputData,
    undefined,
    OutputData
  >({
    resolver: zodResolver(outputSchema),
    defaultValues: { asset: sparseAsset, date: null },
  });

  const assignmentQuery = useQuery({
    queryKey: ['asset', props.asset.key, 'project-history'],
    queryFn: () => getAssetProjectHistory(props.asset.key),
  });

  // Find the current active assignment for the asset
  const currentAssignment = assignmentQuery.data?.history.find(
    ({ end }) => end === null
  );

  const projectName = props.asset.project?.name ?? 'Not assigned';
  const isDisabled =
    unassignAssetMutation.isPending || props.asset.project === null;

  const helperText =
    currentAssignment === undefined
      ? undefined
      : `Assignment effective ${currentAssignment.start.toLocaleString({ dateStyle: 'medium' })}`;

  /**
   * Handles form submission when validation passes.
   * Unassigns the asset from its current project.
   *
   * @param data - The validated form data
   */
  const handleValidSubmission: SubmitHandler<OutputData> = (data) => {
    unassignAssetMutation.mutate(data);
  };

  /**
   * Closes the dialog and resets the form state.
   * Restores default values for the next use.
   */
  const close = () => {
    if (unassignAssetMutation.isPending) return;

    reset({ asset: sparseAsset, date: null });
    props.onClose();
  };

  return (
    <>
      <SupportSnackbar
        isOpen={unassignAssetMutation.isError}
        message="An error occurred while unassigning the asset."
        onClose={unassignAssetMutation.reset}
      />
      <Dialog
        fullWidth
        maxWidth="xs"
        open={props.isOpen}
        onClose={close}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(handleValidSubmission),
        }}
      >
        <DialogTitle>Unassign asset from project</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <FormSparseAssetSearch
              name="asset"
              label="Asset"
              control={control}
              readOnly
            />
            <TextField
              disabled
              size="small"
              label="Project"
              value={projectName}
              helperText={helperText}
            />
            <FormDatePicker
              disableFuture
              name="date"
              label="Effective date"
              control={control}
              disabled={isDisabled}
              minimumDate={currentAssignment?.start}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            disabled={isDisabled}
          >
            Unassign
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
